import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import s from './onboarding.module.scss';
import scheduleS from './schedule.module.scss';
import classNames from 'classnames';
import { Button } from '@shared/ui/button/button';
import { IconChevronDown, IconSquare, IconSquareFilled } from '@tabler/icons-react';
import { useTimeSlots } from '@features/schedule/api/useTimeSlots';
import { useUpdateTimeSlots } from '@features/teacher/edit-schedule/api/useUpdateTimeSlots';
import { itemVariants, sectionVariants } from '@shared/lib/onboarding-motion';

const ALL_DAYS = [
	{ en: 'monday', ru: 'Пн' },
	{ en: 'tuesday', ru: 'Вт' },
	{ en: 'wednesday', ru: 'Ср' },
	{ en: 'thursday', ru: 'Чт' },
	{ en: 'friday', ru: 'Пт' },
	{ en: 'saturday', ru: 'Сб' },
	{ en: 'sunday', ru: 'Вс' },
];

const PERIODS = [
	{ id: 'morning', label: 'Утро', range: '6:00–11:00', start: 6, end: 11 },
	{ id: 'day', label: 'День', range: '12:00–17:00', start: 12, end: 17 },
	{ id: 'evening', label: 'Вечер', range: '18:00–23:00', start: 18, end: 23 },
];

const formatHour = (hour) => `${hour < 10 ? '0' : ''}${hour}:00`;

export const Schedule = ({ setCurrentStep }) => {
	const { data: savedTimeSlots } = useTimeSlots(true);
	const { mutate: updateTimeSlots, isPending } = useUpdateTimeSlots();

	const initialSlots = useMemo(() => savedTimeSlots || [], [savedTimeSlots]);

	const [checkedDays, setCheckedDays] = useState(initialSlots);
	const [expandedPeriod, setExpandedPeriod] = useState(null);

	useEffect(() => {
		if (savedTimeSlots && checkedDays.length === 0) {
			setCheckedDays(savedTimeSlots);
		}
	}, [savedTimeSlots, checkedDays.length]);

	const isSlotChecked = (weekday, time) =>
		checkedDays.some((item) => item.weekday === weekday && item.time === time);

	const slotsForDayAndPeriod = (period, day) => {
		const slots = [];
		for (let h = period.start; h <= period.end; h++) {
			slots.push({ weekday: day, time: formatHour(h) });
		}
		return slots;
	};

	const dayPeriodState = (period, day) => {
		const slots = slotsForDayAndPeriod(period, day);
		const selectedCount = slots.filter((slot) => isSlotChecked(slot.weekday, slot.time)).length;
		if (selectedCount === 0) return 'empty';
		if (selectedCount === slots.length) return 'full';
		return 'partial';
	};

	const toggleDayPeriod = (period, day) => {
		const slots = slotsForDayAndPeriod(period, day);
		const state = dayPeriodState(period, day);
		const isFull = state === 'full';
		setCheckedDays((prev) => {
			if (isFull) {
				return prev.filter(
					(item) => !slots.some((slot) => slot.weekday === item.weekday && slot.time === item.time)
				);
			}
			const existingKeys = new Set(prev.map((item) => `${item.weekday}-${item.time}`));
			const newSlots = slots.filter((slot) => !existingKeys.has(`${slot.weekday}-${slot.time}`));
			return [...prev, ...newSlots];
		});
	};

	const toggleSlot = (weekday, time) => {
		setCheckedDays((prev) => {
			const index = prev.findIndex((item) => item.weekday === weekday && item.time === time);
			if (index !== -1) {
				return prev.filter((_, i) => i !== index);
			}
			return [...prev, { weekday, time }];
		});
	};

	const togglePeriod = (periodId) => {
		setExpandedPeriod((prev) => (prev === periodId ? null : periodId));
	};

	const onSubmit = () => {
		updateTimeSlots(
			{ timeslots: checkedDays },
			{
				onSuccess: () => setCurrentStep((prev) => prev + 1),
			}
		);
	};

	return (
		<motion.div className={s.slideWrapper} variants={sectionVariants} initial="initial" animate="animate">
			<motion.div className={s.titleWrapper} variants={itemVariants}>
				<p className={s.title}>Ваше расписание</p>
				<p className={s.subtitle}>
					Отметьте удобное время. Нажмите на строку, чтобы выбрать часы детально. <br />
					Изменить расписание можно будет в настройках
				</p>
			</motion.div>

			<motion.div className={scheduleS.scheduleContainer} variants={itemVariants}>
				<div className={scheduleS.headerRow}>
					<div className={scheduleS.headerLabel}></div>
					{ALL_DAYS.map((day) => (
						<div key={day.en} className={scheduleS.headerDay}>
							{day.ru}
						</div>
					))}
				</div>

				{PERIODS.map((period) => {
					const isExpanded = expandedPeriod === period.id;
					const hours = [...Array(period.end - period.start + 1)].map((_, i) =>
						formatHour(period.start + i)
					);

					return (
						<motion.div
							key={period.id}
							className={scheduleS.periodBlock}
							variants={itemVariants}
							>
							<div
								className={classNames(scheduleS.periodHeader, isExpanded && scheduleS.expanded)}
							>
								<button
									type="button"
									className={scheduleS.periodLabel}
									onClick={() => togglePeriod(period.id)}
								>
									<span className={scheduleS.periodLabelText}>
										{period.label}
										<span className={scheduleS.periodLabelRange}>{period.range}</span>
									</span>
									<IconChevronDown
										className={classNames(scheduleS.chevron, isExpanded && scheduleS.rotated)}
										size={16}
									/>
								</button>
								{ALL_DAYS.map((day) => {
									const state = dayPeriodState(period, day.en);
									return (
										<button
											type="button"
											key={day.en}
											className={classNames(
												scheduleS.summaryCell,
												state === 'full' && scheduleS.full,
												state === 'partial' && scheduleS.partial
											)}
											onClick={() => toggleDayPeriod(period, day.en)}
										>
											{state === 'full' && <IconSquareFilled size={12} />}
											{state === 'partial' && <IconSquareFilled size={12} />}
											{state === 'empty' && <IconSquare size={12} />}
										</button>
									);
								})}
							</div>

							<AnimatePresence initial={false}>
								{isExpanded && (
									<motion.div
										className={scheduleS.periodDetail}
										initial={{ height: 0, opacity: 0 }}
										animate={{ height: 'auto', opacity: 1 }}
										exit={{ height: 0, opacity: 0 }}
										transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
									>
									<div className={scheduleS.detailGrid}>
										{hours.map((time) => (
											<React.Fragment key={time}>
												<div className={scheduleS.timeCell}>{time}</div>
												{ALL_DAYS.map((day) => {
													const checked = isSlotChecked(day.en, time);
													return (
														<button
															type="button"
															key={day.en + time}
															className={classNames(
																scheduleS.slotBtn,
																checked && scheduleS.active
															)}
															onClick={() => toggleSlot(day.en, time)}
														>
															{checked ? (
																<IconSquareFilled size={14} />
															) : (
																<IconSquare size={14} />
															)}
														</button>
													);
												})}
											</React.Fragment>
										))}
									</div>
									</motion.div>
								)}
							</AnimatePresence>
						</motion.div>
					);
				})}
			</motion.div>

			<motion.div className={s.btns} variants={itemVariants}>
				<Button
					label="Назад"
					size="small"
					theme="secondary"
					className={s.button}
					onClick={() => setCurrentStep((prev) => prev - 1)}
				/>
				<Button
					label="Далее"
					size="small"
					className={s.button}
					disabled={checkedDays.length === 0}
					fetching={isPending}
					onClick={onSubmit}
				/>
			</motion.div>
		</motion.div>
	);
};
